import {
  TableModel,
  tableModelFactory,
  TableRowId,
} from "../table-model-factory";
import { Actions, Computed, computed, Thunk, thunk } from "easy-peasy";
import {
  ActionPayload_EcpCustomerInteraction,
  submitUserAction__ecp_customer_interaction,
} from "../../user-actions/submit-user-action__ecp_customer_interaction";
import _ from "lodash";
import { toaster } from "../../toaster";
import { UserAction } from "../../user-actions/user_actions";
import { StoreModel } from "../model";
import { Injections } from "../store-injections";
import moment from "moment-timezone";

export type UserAction_EcpCustomerInteraction = UserAction<
  "ecp_customer_interaction",
  ActionPayload_EcpCustomerInteraction
>;
export interface EcpCustomerInteractionModel
  extends Omit<
    TableModel<UserAction_EcpCustomerInteraction>,
    "rowIds" | "rowExists"
  > {
  handleReportCustomerInteraction: Thunk<
    EcpCustomerInteractionModel,
    ActionPayload_EcpCustomerInteraction,
    Injections,
    StoreModel
  >;
  byCustomerId: Computed<
    EcpCustomerInteractionModel,
    { [key: number]: UserAction_EcpCustomerInteraction[] },
    StoreModel
  >;
  customerId_to_latest: Computed<
    EcpCustomerInteractionModel,
    { [key: number]: UserAction_EcpCustomerInteraction },
    StoreModel
  >;
  rowIds: Computed<EcpCustomerInteractionModel, Set<TableRowId>, StoreModel>;
  rowExists: Computed<
    EcpCustomerInteractionModel,
    { (row: UserAction_EcpCustomerInteraction): boolean },
    StoreModel
  >;
}

export function getEcpCustomerInteractionModel(): EcpCustomerInteractionModel {
  return {
    ...tableModelFactory(
      "ecp_customerInteraction",
      "ecp/customer_interactions",
      (row) => row.id
    ),
    handleReportCustomerInteraction: thunk(
      async (
        actions: Actions<EcpCustomerInteractionModel>,
        userActionPayload: ActionPayload_EcpCustomerInteraction,
        { getState }
      ) => {
        const state = getState();
        // @ts-ignore
        const maxRowId = _.max([...state.rowIds]) ?? 0;
        // const rowId = state.GET_ROW_ID(userActionPayload);
        // const oldRow = state.getRow(rowId);
        const nextRowId = maxRowId + 1;
        actions.upsertRow({
          id: nextRowId,
          action_payload: userActionPayload,
        });
        try {
          const { data } = await submitUserAction__ecp_customer_interaction(
            userActionPayload
          );
          toaster.success("Successfully reported customer interaction", 2);
          actions.deleteRow(nextRowId);
          actions.upsertRow(data);
        } catch (e) {
          actions.deleteRow(nextRowId);
          toaster.warning(
            "Failed to report customer interaction -- please try again"
          );
          throw Error();
        }
      }
    ),
    byCustomerId: computed(
      [(s) => s.initialData],
      (initialData: UserAction_EcpCustomerInteraction[]) =>
        _.groupBy(initialData, "action_payload.customer_snapshot.customer_id")
    ),
    customerId_to_latest: computed(
      [(s) => s.byCustomerId],
      (byCustomerId: { [key: string]: UserAction_EcpCustomerInteraction[] }) =>
        Object.fromEntries(
          Object.entries(byCustomerId).map(([customerId, itxns]) => [
            customerId,
            _.maxBy(itxns, "submitted_at"),
          ])
        )
    ),
    rowIds: computed(
      [(s) => s.initialData, (s) => s.GET_ROW_ID],
      (initialData, GET_ROW_ID) =>
        new Set(initialData.map((row) => GET_ROW_ID(row)))
    ),
    rowExists: computed(
      [(s) => s.rowIds, (s) => s.GET_ROW_ID],
      (rowIds, GET_ROW_ID) => (row) => rowIds.has(GET_ROW_ID(row))
    ),
  };
}

const PACIFIC_TIME = "America/Los_Angeles";

export function happenedToday(itxnTimestamp?: string): boolean {
  if (_.isNull(itxnTimestamp) || _.isUndefined(itxnTimestamp)) {
    return false;
  }
  const itxnTimestampPT = moment(itxnTimestamp).tz(PACIFIC_TIME);
  const currentTimestampPT: moment.Moment = moment.utc().tz(PACIFIC_TIME);

  const minTimestampPT =
    getMinTimestampToQualifyAsHappenedTodayPT(currentTimestampPT);

  // console.log({
  //   itxnTimestamp,
  //   itxnTimestampPT: isoformat(itxnTimestampPT),
  //   currentTimestampPT: isoformat(currentTimestampPT),
  //   minTimestampPT: isoformat(minTimestampPT),
  // });
  return itxnTimestampPT >= minTimestampPT;
}
export function happenedWithinPastTwoWeeks(itxnTimestamp?: string): boolean {
  if (_.isNull(itxnTimestamp) || _.isUndefined(itxnTimestamp)) {
    return false;
  }
  const itxnTimestampPT = moment(itxnTimestamp).tz(PACIFIC_TIME);
  const currentTimestampPT = moment.utc().tz(PACIFIC_TIME);

  const minTimestampPT = currentTimestampPT.subtract(14, "day");

  // console.log({
  //   itxnTimestamp,
  //   itxnTimestampPT: isoformat(itxnTimestampPT),
  //   currentTimestampPT: isoformat(currentTimestampPT),
  //   minTimestampPT: isoformat(minTimestampPT),
  // });
  return itxnTimestampPT >= minTimestampPT;
}

function getMinTimestampToQualifyAsHappenedTodayPT(
  currentTimestampPT: moment.Moment
): moment.Moment {
  const currentHourIndexPT = currentTimestampPT.hour();
  // console.log({ currentHourIndexPT });

  let ret = currentTimestampPT.clone();
  if (currentHourIndexPT < 4) {
    ret = ret.subtract(1, "day");
  }
  return ret.set({
    hour: 4,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}
